import { useEffect, useRef } from "react";

interface UsePollingOptions {
  /**
   * Polling interval in milliseconds
   */
  interval: number;
  /**
   * Whether polling is enabled
   */
  enabled?: boolean;
  /**
   * Whether to run the callback immediately on mount
   */
  runOnMount?: boolean;
}

/**
 * A React hook that polls a callback function at a specified interval
 *
 * @param callback - The function to call on each poll
 * @param options - Polling configuration options
 *
 * @example
 * ```tsx
 * usePolling(async () => {
 *   const data = await fetchData();
 *   setData(data);
 * }, {
 *   interval: 5000,
 *   enabled: isAuthenticated,
 *   runOnMount: true
 * });
 * ```
 */
export const usePolling = (callback: () => void | Promise<void>, options: UsePollingOptions) => {
  const { interval, enabled = true, runOnMount = false } = options;
  const savedCallback = useRef(callback);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    if (!enabled) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    const tick = () => {
      savedCallback.current();
    };

    // Run immediately if requested
    if (runOnMount) {
      tick();
    }

    // Set up interval
    intervalRef.current = setInterval(tick, interval);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [interval, enabled, runOnMount]);
};

export default usePolling;
